// @flow

export type LinkType =
  // An internal link to another article in the docs
  | 'docs'
  // A Stripe reference site (apiref, jsref, cliref, etc.)
  | 'reference'
  // dashboard.stripe.com
  | 'dashboard'
  // support.stripe.com
  | 'support'
  // Another non dashboard or support Stripe property (eg. the marketing site).
  | 'stripe'
  // A link to a third-party site
  | 'thirdparty'
  // An unknown link type. For example, can be a link that has no path which is used as
  // part of a component, or a link to an anchor
  | 'other';

const STRIPE_HOST_REGEX = /stripe\.(me|com)/;
const DOCS_PATH_REGEX = /^\/docs/;
const DASHBOARD_HOST_REGEX = /dashboard\.stripe|-manage-.*dev.stripe/;

// Match /docs/api , and /docs/cli as refs, but not paths like /docs/climate
const REF_PATH_REGEX = /^\/docs\/(api|cli|js|stripe-js\/reference)(\/.*|$)/;

export class LinkInfo {
  url: URL;

  type: LinkType;

  baseUrl: string;

  constructor(urlOrPathname: string, baseUrl: string) {
    this.baseUrl = baseUrl;
    this.url = new URL(urlOrPathname, baseUrl);
    this.type = this._getLinkType(this.url);
  }

  get isExternal() {
    return this.type !== 'docs';
  }

  _getLinkType(url: URL): LinkType {
    const {hostname, pathname, origin} = url;
    if (hostname && !hostname.match(STRIPE_HOST_REGEX)) {
      return 'thirdparty';
    }
    if (pathname) {
      if (pathname.match(REF_PATH_REGEX)) {
        return 'reference';
      }
      if (pathname.match(DOCS_PATH_REGEX)) {
        return 'docs';
      }
    }

    if (hostname.match(DASHBOARD_HOST_REGEX)) {
      return 'dashboard';
    }

    if (hostname === 'support.stripe.com') {
      return 'support';
    }

    // some <a> tags with empty href's are used in interactive components
    // (like tabs), which will have a pathname of '/', but we do not want to
    // consider links like support.stripe.com/ to be 'empty' even though they
    // will have a pathname of '/'
    const isEmptyLink =
      origin === this.baseUrl && (pathname === '' || pathname === '/');

    if (isEmptyLink) {
      return 'other';
    } else {
      return 'stripe';
    }
  }
}
